import { Button, Text, makeStyles, tokens } from '@fluentui/react-components';

import { ErrorState, LoadingState, SectionCard, StatusPill } from '@/design-system';
import { useHubToast } from '@/design-system/toast';
import { spacing } from '@/theme/tokens';

import { useDecideElevation, useElevationPresets, usePendingElevationRequests } from '../api';
import { presetDisplayName } from '../presetDisplayName';

const useStyles = makeStyles({
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: spacing.s,
    padding: spacing.m,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    marginBottom: spacing.s,
  },
  meta: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  actions: {
    display: 'flex',
    gap: spacing.xs,
  },
  scope: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '12px',
    overflowWrap: 'anywhere',
  },
});

export function PendingDecisions() {
  const classes = useStyles();
  const toast = useHubToast();
  const decide = useDecideElevation();
  const presets = useElevationPresets();
  const query = usePendingElevationRequests();

  const handle = (id: string, approve: boolean) => {
    const reason = window.prompt(approve ? 'Approve note:' : 'Reject reason:');
    if (!reason) return;
    decide.mutate(
      { id, decision: { approve, reason } },
      {
        onSuccess: () =>
          { toast({
            intent: approve ? 'success' : 'warning',
            title: approve ? 'Approved' : 'Rejected',
            body: id.slice(0, 8),
          }); },
        onError: (err) =>
          { toast({ intent: 'error', title: 'Decision failed', body: err.message }); },
      },
    );
  };

  if (query.isLoading) {
    return (
      <SectionCard title="Pending decisions" description="Loading…">
        <LoadingState label="Loading pending requests…" />
      </SectionCard>
    );
  }

  if (query.error) {
    return (
      <SectionCard title="Pending decisions" description="Couldn't load the pending list.">
        <ErrorState
          title="Couldn't load pending requests"
          description={query.error instanceof Error ? query.error.message : undefined}
          retry={() => void query.refetch()}
        />
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Pending decisions"
      description={
        query.data?.length === 0
          ? 'Nothing pending in the selected environment.'
          : `${String(query.data?.length ?? 0)} request(s) waiting for your approval.`
      }
    >
      {(query.data ?? []).map((r) => (
        <div key={r.id} className={classes.row}>
          <div>
            <Text weight="semibold">
              {presetDisplayName(r.presetKey, presets.data)}
              {presetDisplayName(r.presetKey, presets.data) !== r.presetKey && (
                <Text as="span" size={200} className={classes.meta}> ({r.presetKey})</Text>
              )} · {r.env}
            </Text>{' '}
            <StatusPill kind="unknown" label="Pending" />
            <div className={classes.meta}>
              {r.requesterUpn} · {String(Math.round(r.durationMs / 60_000))}m ·{' '}
              {new Date(r.createdAt).toLocaleString()}
            </div>
            <div className={classes.scope}>{r.scope}</div>
            <Text size={200} style={{ marginTop: spacing.xs, display: 'block' }}>
              {r.justification}
            </Text>
          </div>
          <div className={classes.actions}>
            <Button
              size="small"
              appearance="primary"
              onClick={() => { handle(r.id, true); }}
              disabled={decide.isPending}
            >
              Approve
            </Button>
            <Button
              size="small"
              appearance="subtle"
              onClick={() => { handle(r.id, false); }}
              disabled={decide.isPending}
            >
              Reject
            </Button>
          </div>
        </div>
      ))}
    </SectionCard>
  );
}
